import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  MenuItem,
  Pagination,
  Checkbox,
  Typography,
  IconButton,
  FormControl,
  Select,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Chip,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import SearchBarContainer from "./SearchBarContainer";
import { makeStyles, useTheme } from "@mui/styles";
import Header from "./Header";
import PaginationSettingsModal from "./SettingsModal";

const useStyles = makeStyles((theme) => ({
  gridCard: {
    backgroundColor: theme.palette.secondary.main,
    border: `1px solid ${theme.palette.border.color}`,
    borderRadius: "8px",
    transition: "all 0.2s ease-in-out",
    cursor: "pointer",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      transform: "translateY(-2px)",
      boxShadow: `0 4px 12px rgba(0, 107, 206, 0.15)`,
    },
  },
  selectedCard: {
    borderColor: "#006bce !important",
    backgroundColor: `${theme.palette.primary.main}10 !important`,
  },
  cardHeader: {
    borderBottom: `1px solid ${theme.palette.border.color}`,
    paddingBottom: "8px",
    marginBottom: "12px",
  },
}));

const RenderGridItem = (column, row, object) => {
  const getValue = () => {
    if (column.render && typeof column.render === "function") {
      return column.render(row);
    }

    const value = row[column.id];
    if (value === null || value === undefined || value === "") {
      return column.default || "N/A";
    }

    return value;
  };

  const displayValue = getValue();

  switch (column.type) {
    case "textField":
    case "text":
      return (
        <Typography
          variant="body2"
          sx={{
            color: "#fff",
            fontSize: "13px",
            wordBreak: "break-word",
          }}
        >
          {displayValue}
        </Typography>
      );
    case "checkbox":
      return (
        <Checkbox
          color="primary"
          indeterminate={column.indeterminate}
          checked={column.checked}
          onChange={column.onChange}
          sx={{
            color: "#f5f5f5",
            "& .MuiSvgIcon-root": {
              fontSize: "16px",
            },
            "&.Mui-checked": { color: "#006bce" },
            "&.MuiCheckbox-indeterminate": { color: "#006bce" },
            padding: "4px",
          }}
        />
      );
    case "icon":
      return (
        <column.icon
          sx={{
            color: "#fff",
            fontSize: "18px",
          }}
        />
      );
    case "button":
      return (
        <Chip
          label={column.label}
          onClick={column.onClick}
          variant="outlined"
          size="small"
          sx={{
            color: "#fff",
            borderColor: "#fff",
            fontSize: "12px",
            height: "24px",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        />
      );
    case "link":
      return (
        <Typography
          component="a"
          href="#"
          onClick={column.onClick}
          variant="body2"
          sx={{
            color: "#006bce",
            fontSize: "13px",
            textDecoration: "none",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {column.label}
        </Typography>
      );
    case "image":
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "8px",
          }}
        >
          <img
            src={column.src || "/placeholder.svg"}
            alt={column.alt}
            style={{
              width: column.width || "40px",
              height: column.height || "40px",
              objectFit: column.objectFit || "cover",
              borderRadius: column.borderRadius || "4px",
              cursor: column.cursor || "pointer",
            }}
          />
        </Box>
      );
    case "dropDown":
      const option = column.options?.find((opt) => opt.value === displayValue);
      return (
        <Chip
          label={option ? option.label : displayValue}
          size="small"
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            color: "#fff",
            fontSize: "12px",
            height: "20px",
          }}
        />
      );
    case "date":
      const dateValue = displayValue;
      const isValidDate =
        dateValue && dateValue !== "N/A" && !isNaN(new Date(dateValue));

      return (
        <Typography
          variant="body2"
          sx={{
            color: "#fff",
            fontSize: "13px",
          }}
        >
          {isValidDate
            ? new Date(dateValue).toLocaleDateString("en-US", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
            : displayValue}
        </Typography>
      );
    case "geoJson":
      const latitude = row[`${object}Latitude`];
      const longitude = row[`${object}Longitude`];
      return (
        <Typography
          component="a"
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{
            color: "#006bce",
            textDecoration: "none",
            fontSize: "13px",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {latitude && longitude
            ? `${latitude}, ${longitude}`
            : column.default || "N/A"}
        </Typography>
      );
    case "number":
      return (
        <Typography
          variant="body2"
          sx={{
            color: "#fff",
            fontSize: "13px",
            fontWeight: 500,
          }}
        >
          {displayValue}
        </Typography>
      );
    case "plan":
      const planValue = displayValue;
      const teamSizeMapping = { free: 3, pro: 10, enterprise: 20 };
      const teamSize =
        teamSizeMapping[planValue?.toLowerCase()] || column.default;

      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: "#fff",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            {planValue === "N/A" ? column.default || "N/A" : planValue}
          </Typography>
          {planValue !== "N/A" && (
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                fontSize: "11px",
              }}
            >
              {teamSize} users
            </Typography>
          )}
        </Box>
      );
    default:
      return (
        <Typography
          variant="body2"
          sx={{
            color: "#fff",
            fontSize: "13px",
            wordBreak: "break-word",
          }}
        >
          {displayValue}
        </Typography>
      );
  }
};

// Sorting utility functions (same as ListComponent)
const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const descendingComparator = (a, b, orderBy) => {
  const aValue = a[orderBy];
  const bValue = b[orderBy];
  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const GridComponent = (props) => {
  const {
    header = true,
    Icon,
    objectDefinition,
    title,
    showAddButton,
    addButtonText,
    setShowAddForm,
    showActions,
    actionOptions,
    selectedAction,
    onAddClick,
    onDeleteClick,
    onRefresh,
    object,
    columns,
    data,
    selectedRows,
    setSelectedRows,
    setShowUpdateForm,
    layoutView,
    onLayoutChange,
  } = props;

  const theme = useTheme();
  const rowCount = data?.length;
  const numSelected = selectedRows?.length;
  const classes = useStyles();

  // Pagination settings state
  const [paginationSettings, setPaginationSettings] = useState({
    rowsPerPage: 12, // Adjusted for grid view
    showPageInfo: true,
    showFirstLast: true,
    showRowsPerPageSelector: false,
  });

  const [page, setPage] = useState(1);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const totalPages = Math.ceil(data?.length / paginationSettings.rowsPerPage);

  const [gridData, setGridData] = useState(
    data?.slice(
      (page - 1) * paginationSettings.rowsPerPage,
      page * paginationSettings.rowsPerPage
    )
  );

  // Popover state
  const [anchorElOptions, setAnchorElOptions] = useState(null);
  const [anchorElAttributes, setAnchorElAttributes] = useState(null);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [anchorElLayout, setAnchorElLayout] = useState(null);
  const openLayout = Boolean(anchorElLayout);
  const openOptions = Boolean(anchorElOptions);
  const openAttributes = Boolean(anchorElAttributes);
  const openFilter = Boolean(anchorElFilter);

  // Sort state
  const [order] = useState("asc");
  const [orderBy] = useState(columns[0]?.id || "");

  // Column visibility state
  const [visibilityStatus, setVisibilityStatus] = useState(
    columns.reduce((acc, column) => {
      acc[column.id] = true;
      return acc;
    }, {})
  );

  // Check if a row is selected
  const isSelected = (row) => {
    const selectedIndex = selectedRows.findIndex(
      (selected) => selected[`${object}Id`] === row[`${object}Id`]
    );
    return selectedIndex !== -1;
  };

  // Handle form add state
  const handleAddClick = () => {
    // 
    if (objectDefinition) {
      props?.setFormObject?.(objectDefinition);
    } else {
      props?.setFormObject(null);
      console.warn("objectDefinition is undefined");
    }
    setShowAddForm(true);
  };

  // Handle form modify state
  const handleModifyClick = () => {
    setShowUpdateForm(true);
  };

  // Handle Popover open/close
  const handleOptionsClick = (event) => {
    setAnchorElOptions(event.currentTarget);
  };

  const handleOptionsClose = () => {
    setAnchorElOptions(null);
    setAnchorElAttributes(null);
    setAnchorElLayout(null);
  };

  // Handle attributes
  const handleAttributesClick = (event) => {
    setAnchorElAttributes(anchorElOptions);
    setAnchorElOptions(null);
  };

  const handleAttributesClose = () => {
    setAnchorElOptions(anchorElAttributes);
    setAnchorElAttributes(null);
  };

  const handleSettingsClick = () => {
    handleOptionsClose();
  };

  // Handle column visibility toggle
  const handleToggle = (columnId) => (event) => {
    setVisibilityStatus((prev) => ({
      ...prev,
      [columnId]: event.target.checked,
    }));
  };

  // Handle layout click
  const handleLayoutClick = (event) => {
    setAnchorElLayout(anchorElOptions);
    setAnchorElOptions(null);
  };

  const handleLayoutClose = () => {
    setAnchorElOptions(anchorElLayout);
    setAnchorElLayout(null);
  };

  // Handle filter
  const handleFilterClick = useCallback((event) => {
    setAnchorElFilter(event.currentTarget);
  }, []);

  const handleFilterClose = useCallback(() => {
    setAnchorElFilter(null);
  }, []);

  // Select all rows
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedRows([...data]);
      return;
    }
    setSelectedRows([]);
  };

  // Toggle selection of a single row
  const handleSelected = (event, row) => {
    event.stopPropagation();
    const selectedIndex = selectedRows?.findIndex(
      (selected) => selected[`${object}Id`] === row[`${object}Id`]
    );
    const newSelectedRows = [...selectedRows];

    if (selectedIndex === -1) {
      newSelectedRows.push(row);
    } else {
      newSelectedRows.splice(selectedIndex, 1);
    }
    setSelectedRows(newSelectedRows);
    if (newSelectedRows.length > 0) {
      props?.setFormObject?.(newSelectedRows[0]);
    }
  };

  // Handle card click (for row selection)
  const handleCardClick = (row) => {
    const selectedIndex = selectedRows?.findIndex(
      (selected) => selected[`${object}Id`] === row[`${object}Id`]
    );
    const newSelectedRows = [...selectedRows];

    if (selectedIndex === -1) {
      newSelectedRows.push(row);
    } else {
      newSelectedRows.splice(selectedIndex, 1);
    }
    setSelectedRows(newSelectedRows);
    if (newSelectedRows.length > 0) {
      props?.setFormObject?.(newSelectedRows[0]);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = Number.parseInt(event.target.value, 10);
    setPaginationSettings((prev) => ({
      ...prev,
      rowsPerPage: newRowsPerPage,
    }));
    setPage(1);
  };

  // Handle pagination settings update
  const handlePaginationSettingsUpdate = (newSettings) => {
    setPaginationSettings(newSettings);
    setPage(1);
  };

  // Filter visible columns based on visibilityStatus
  const visibleColumns = columns.filter(
    (column) => visibilityStatus[column.id]
  );

  const onSearch = (searchValue) => {
    const filteredData = data.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(searchValue.toLowerCase())
      )
    );
    setGridData(filteredData);
  };


  // Sorted data
  const sortedData = useMemo(() => {
    return stableSort(data || [], getComparator(order, orderBy));
  }, [data, order, orderBy]);

  useEffect(() => {
    // Update gridData whenever sortedData or pagination changes
    const paginatedData = sortedData?.slice(
      (page - 1) * paginationSettings.rowsPerPage,
      page * paginationSettings.rowsPerPage
    );
    setGridData(paginatedData);
  }, [data, sortedData, page, paginationSettings.rowsPerPage]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        width: "100%",
        gap: header && 2,
      }}
    >
      <Header
        Icon={Icon}
        title={title}
        showAddButton={showAddButton}
        addButtonText={addButtonText}
        onAddClick={onAddClick ? onAddClick : handleAddClick}
        showActions={showActions}
        actionOptions={actionOptions}
        selectedAction={selectedAction}
        onDeleteClick={onDeleteClick}
        handleFilterClick={handleFilterClick}
        handleFilterClose={handleFilterClose}
        openFilter={openFilter}
        anchorElFilter={anchorElFilter}
        onRefresh={onRefresh}
        columns={columns}
        openOptions={openOptions}
        anchorElOptions={anchorElOptions}
        handleOptionsClick={handleOptionsClick}
        handleOptionsClose={handleOptionsClose}
        handleAttributesClick={handleAttributesClick}
        handleSettingsClick={handleSettingsClick}
        openAttributes={openAttributes}
        anchorElAttributes={anchorElAttributes}
        handleAttributesClose={handleAttributesClose}
        layoutView={layoutView}
        onLayoutChange={onLayoutChange}
        handleLayoutClick={handleLayoutClick}
        handleLayoutClose={handleLayoutClose}
        openLayout={openLayout}
        anchorElLayout={anchorElLayout}
        visibilityStatus={visibilityStatus}
        handleToggle={handleToggle}
        selectedRows={selectedRows}
        onModifyClick={handleModifyClick}
        object={object}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.tertiary.main,
          borderRadius: 2,
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            backgroundColor: "transparent",
            padding: "18px 12px",
            boxSizing: "border-box",
          }}
        >
          <SearchBarContainer onSearch={onSearch} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Rows per page selector */}
            {paginationSettings.showRowsPerPageSelector && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.color }}
                >
                  Items per page:
                </Typography>
                <FormControl size="small">
                  <Select
                    value={paginationSettings.rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    sx={{
                      color: theme.palette.text.color,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.border.color,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.primary.main,
                      },
                      "& .MuiSvgIcon-root": {
                        color: theme.palette.text.color,
                      },
                      minWidth: 60,
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: theme.palette.secondary.main,
                          color: theme.palette.text.color,
                        },
                      },
                    }}
                  >
                    {[6, 12, 18, 24, 30, 48].map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        sx={{
                          color: theme.palette.text.color,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.main + "20",
                          },
                        }}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Page info */}
            {paginationSettings.showPageInfo && (
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.color }}
              >
                Page {page} of {totalPages}
              </Typography>
            )}

            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              showFirstButton={paginationSettings.showFirstLast}
              showLastButton={paginationSettings.showFirstLast}
              sx={{
                "& .MuiPaginationItem-root": {
                  color: theme.palette.text.color,
                  border: "none",
                  minWidth: 24,
                  height: 24,
                  fontSize: "0.75rem",
                  borderRadius: 0,
                },
                "& .Mui-selected": {
                  backgroundColor: "transparent !important",
                  border: "none",
                  borderBottom: "1px solid #006BCE",
                  color: "#006BCE",
                },
                "& .MuiPaginationItem-previousNext": {
                  border: `1px solid ${theme.palette.border.color}`,
                  borderRadius: "50% !important",
                },
              }}
            />

            {/* Settings icon for pagination */}
            <Tooltip title="Pagination Settings">
              <IconButton
                onClick={() => setSettingsModalOpen(true)}
                sx={{
                  color: theme.palette.text.color,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.main + "20",
                  },
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {/* Select All Checkbox */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={handleSelectAllClick}
            sx={{
              color: "#f5f5f5",
              "& .MuiSvgIcon-root": {
                fontSize: "18px",
              },
              "&.Mui-checked": { color: "#006bce" },
              "&.MuiCheckbox-indeterminate": { color: "#006bce" },
            }}
          />
          <Typography variant="body2" sx={{ color: theme.palette.text.color }}>
            Select All
          </Typography>
        </Box>

        {/* Grid Container */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            padding: "0 12px 12px 12px",
            "&::-webkit-scrollbar": {
              width: "6px",
              height: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.border.color,
              borderRadius: "3px",
            },
          }}
        >
          <Grid container spacing={2}>
            {gridData?.map((row, rowIndex) => {
              const isItemSelected = isSelected(row);
              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={row[`${object}Id`]}
                >
                  <Card
                    className={`${classes.gridCard} ${
                      isItemSelected ? classes.selectedCard : ""
                    }`}
                    onClick={() => handleCardClick(row)}
                  >
                    <CardContent
                      sx={{ padding: "16px !important", position: "relative" }}
                    >
                      {/* Selection Checkbox */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                        }}
                      >
                        <Checkbox
                          checked={isItemSelected}
                          onChange={(event) => handleSelected(event, row)}
                          sx={{
                            color: theme.palette.text.color,
                            "&.Mui-checked": { color: "#006bce" },
                            "& .MuiSvgIcon-root": {
                              fontSize: "16px",
                            },
                            padding: "4px",
                          }}
                        />
                      </Box>

                      {/* Card Content */}
                      <Box sx={{ marginTop: "20px" }}>
                        {visibleColumns.map((column, index) => (
                          <Box
                            key={column.id}
                            sx={{
                              marginBottom:
                                index === visibleColumns.length - 1 ? 0 : 1.5,
                              display: "flex",
                              flexDirection: column,
                              gap: 0.5,
                            }}
                          >
                            {/* Column Label */}
                            <Typography
                              variant="caption"
                              sx={{
                                color: "rgba(255, 255, 255, 0.7)",
                                fontSize: "11px",
                                fontWeight: 500,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              {column.label}
                            </Typography>

                            {/* Column Value */}
                            <Box>
                              {RenderGridItem(
                                {
                                  ...column,
                                  value: row[column.id],
                                  coordinates: row?.imageGeoHash?.coordinates,
                                },
                                row,
                                object
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Empty State */}
          {(!gridData || gridData.length === 0) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.color,
                  opacity: 0.6,
                }}
              >
                No data available
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.color,
                  opacity: 0.4,
                }}
              >
                Try adjusting your search or filters
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Pagination Settings Modal */}
      <PaginationSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        currentSettings={paginationSettings}
        onSettingsUpdate={handlePaginationSettingsUpdate}
      />
    </Box>
  );
};

export default GridComponent;
